/**
 * The slice of a node the sort needs. Satisfied by the canvas's React Flow
 * nodes and by run-snapshot DTOs once the caller maps them.
 */
export interface TopoNode {
  readonly id: string;
}

export interface TopoEdge {
  readonly source: string;
  readonly target: string;
}

/**
 * Rank of a node among those the sort could emit next. The lowest rank wins,
 * so this is what decides the order of independent branches.
 */
export type TopoRank = (nodeId: string, index: number) => number;

/** Remove and return the ready node the caller ranks first. */
function takeNext(ready: string[], rankOf: (id: string) => number): string {
  let best = 0;
  for (let i = 1; i < ready.length; i++) {
    if (rankOf(ready[i]) < rankOf(ready[best])) best = i;
  }
  return ready.splice(best, 1)[0];
}

/** Decrement in-degrees for neighbours and return those that become ready. */
function drainNeighbours(
  current: string,
  adjacency: Map<string, string[]>,
  inDegree: Map<string, number>,
): string[] {
  const ready: string[] = [];
  for (const neighbour of adjacency.get(current) ?? []) {
    const deg = (inDegree.get(neighbour) ?? 1) - 1;
    inDegree.set(neighbour, deg);
    if (deg === 0) ready.push(neighbour);
  }
  return ready;
}

/** Append any cycle-trapped nodes so the caller always gets the full set of IDs. */
function appendCycleNodes(result: string[], ids: string[]): void {
  if (result.length >= ids.length) return;
  const seen = new Set(result);
  for (const id of ids) {
    if (!seen.has(id)) result.push(id);
  }
}

/**
 * Return node IDs in topological (execution) order using Kahn's algorithm.
 *
 * Nodes with no incoming edges come first. Whenever several nodes could come
 * next, {@code rank} picks between them — by default the original array
 * order. Cycles are tolerated: any nodes caught in one are appended at the
 * end in their original order so the caller always gets the full set of IDs
 * back.
 */
export function topoSortNodeIds(
  nodes: readonly TopoNode[],
  edges: readonly TopoEdge[],
  rank?: TopoRank,
): string[] {
  const ids = nodes.map((n) => n.id);
  const idSet = new Set(ids);
  const indexMap = new Map<string, number>();
  for (let i = 0; i < ids.length; i++) indexMap.set(ids[i], i);
  const rankOf = (id: string): number => {
    const index = indexMap.get(id) ?? 0;
    return rank ? rank(id, index) : index;
  };

  const inDegree = new Map<string, number>();
  const adjacency = new Map<string, string[]>();
  for (const id of ids) {
    inDegree.set(id, 0);
    adjacency.set(id, []);
  }

  for (const edge of edges) {
    if (!idSet.has(edge.source) || !idSet.has(edge.target)) continue;
    adjacency.get(edge.source)!.push(edge.target);
    inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1);
  }

  const ready: string[] = [];
  for (const id of ids) {
    if (inDegree.get(id) === 0) ready.push(id);
  }

  const result: string[] = [];
  while (ready.length > 0) {
    const current = takeNext(ready, rankOf);
    result.push(current);
    ready.push(...drainNeighbours(current, adjacency, inDegree));
  }

  appendCycleNodes(result, ids);
  return result;
}
